import {
  AloftArea,
  AloftPrice,
  CraftsmanAdjustmentProps,
  ORDER_FEATURES,
  getCraftsmanAdjustment,
  isDefined,
  isUndefined,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { selectCompSequence } from '../orderSelectors.ts';
import { selectImprovementsEntities } from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectPropertyIdentificationEntities } from '../propertyDataSelectors/propertyIdentificationSelectors.ts';
import { selectDepreciationRatiosForFeature } from '../selectDepreciationRatioForFeature.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { selectAdjustmentProps } from './selectAdjustmentProps.ts';

const calculatePoolAdj = (
  adjustmentProps: CraftsmanAdjustmentProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  depreciationRatios: ReturnType<typeof selectDepreciationRatiosForFeature>,
  compID: string,
) => {
  const subjectImprovements = improvements.subject;
  const compImprovements = improvements[compID];

  const subjectPropertyIdentification = propertyIdentification.subject;
  const compPropertyIdentification = propertyIdentification[compID];

  if (
    isUndefined(subjectImprovements) ||
    isUndefined(compImprovements) ||
    isUndefined(depreciationRatios) ||
    isUndefined(subjectPropertyIdentification) ||
    isUndefined(compPropertyIdentification)
  ) {
    return undefined;
  }

  const depreciationRatio = depreciationRatios.subject;
  const compDepreciationRatio = depreciationRatios[compID];

  if (isUndefined(depreciationRatio) || isUndefined(compDepreciationRatio)) {
    return undefined;
  }

  const { pool } = subjectImprovements;
  const { pool: compPool } = compImprovements;
  const {
    address: { PostalCode: zipCode },
  } = subjectPropertyIdentification;
  const {
    address: { PostalCode: compZipCode },
  } = compPropertyIdentification;

  if (isUndefined(zipCode) || isUndefined(compZipCode)) {
    return undefined;
  }

  const adjCalculator = getCraftsmanAdjustment(adjustmentProps);

  return adjCalculator.getPoolAdj({
    subject: {
      hasFeature: ORDER_FEATURES.pool.hasFeature(subjectImprovements),
      sqft: pool?.area ? AloftArea.fromNumber(pool.area.unitAmount, pool.area.unit) : undefined,
      quality: pool?.quality,
      zipCode,
    },
    comp: {
      hasFeature: ORDER_FEATURES.pool.hasFeature(compImprovements),
      sqft: compPool?.area
        ? AloftArea.fromNumber(compPool.area.unitAmount, compPool.area.unit)
        : undefined,
      quality: compPool?.quality,
      zipCode: compZipCode,
    },
    depreciationRatio,
    compDepreciationRatio,
  }).adjustment;
};

export const selectPoolAdj = createPriceSelector(
  [
    selectAdjustmentProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state) => selectDepreciationRatiosForFeature(state, 'pool'),
    (state, id: string) => id,
  ],
  calculatePoolAdj,
);

export const selectPoolAdjs = createSelector(
  [
    selectAdjustmentProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state) => selectDepreciationRatiosForFeature(state, 'pool'),
    selectCompSequence,
  ],
  (adjustmentVersion, improvements, propertyIdentification, depreciationRatios, compSequence) =>
    compSequence.reduce((acc: Record<string, AloftPrice>, propID) => {
      const adj = calculatePoolAdj(
        adjustmentVersion,
        improvements,
        propertyIdentification,
        depreciationRatios,
        propID,
      );

      if (isDefined(adj)) acc[propID] = adj;

      return acc;
    }, {}),
);
